import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {styled} from '@packages/utilities/styled';
import NumberInput from '@packages/systems/design-system/WebflowNumberInput';
import {FlexRow} from '@packages/systems/design-system/Layout';
import {TypeImplementation, Types} from '@packages/systems/wfdl';
import {EUnion, ERecord, ENumber, TNumber} from '@packages/systems/wfdl-ast';
import {TOption} from '@packages/systems/plugins/Option';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';

const dispatchSomeNumber = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) => (num: number) => {
    dispatch(
      updateExpressionAction(
        EUnion({
          tag: 'Some',
          value: ENumber(num),
        })
      )
    );
  }
);

const dispatchNone = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) => () => {
    dispatch(
      updateExpressionAction(
        EUnion({
          tag: 'None',
          value: ERecord({}),
        })
      )
    );
  }
);

const underlyingType = TOption(TNumber);

const OptionalNumberRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const stripped = stripAnn(expr);

  const type = getAnn(expr)?.type || underlyingType;
  const union = ContextualExpressions.getUnion(stripped);
  const number =
    union && union.tag === 'Some'
      ? ContextualExpressions.getNumber(stripAnn(union.value))
      : null;
  const value = number !== null ? number : undefined;

  if (value !== undefined || union || ContextualExpressions.isHole(stripped)) {
    return (
      <FlexRow>
        <Input
          data-automation-id={Types.generateAutomationIds(type)}
          onChange={dispatchSomeNumber(dispatch)}
          onReset={dispatchNone(dispatch)}
          value={value}
          optional
          step={Number.MIN_VALUE}
          min={Number.NEGATIVE_INFINITY}
          max={Number.POSITIVE_INFINITY}
          type="text"
          flexGrow={1}
          placeholder="Enter a number..."
        />
      </FlexRow>
    );
  }
  return <ExpressionEditor expression={expr} dispatch={dispatch} />;
};

export const OptionalNumber = TypeImplementation.Alias({
  type: underlyingType,
  render: OptionalNumberRender,
});

// @ts-expect-error - TS2769 - No overload matches this call.
const Input = styled(NumberInput)({
  width: '100%',
});
